import React, { useState } from "react";
import {
  Play,
  Pencil,
  Trash2,
  Plus,
  RefreshCw,
  Zap,
  Clock,
  Calendar,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import DeleteConfirmationModal from "../../components/Common/DeleteConfirmationModal";
import { isActiveAutomationStatus } from "../../services/automationService";
import { getWorkflowKeyLabel } from "./automationConstants";

const formatScheduler = (value) => {
  if (value == null || value === "") return "—";
  const hours = Number(value);
  if (Number.isNaN(hours) || hours === 0) return "Manual only";
  const rounded = Number.isInteger(hours) ? hours : Math.round(hours * 10) / 10;
  if (rounded === 1) return "Every hour";
  return `Every ${rounded} hours`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const getAutomationError = (item) => {
  const response = item?.response;
  if (!response) return null;
  if (typeof response === "string" && response.trim()) return response.trim();
  if (typeof response !== "object") return null;
  const message = response.error ?? response.message ?? response.detail;
  return typeof message === "string" && message.trim() ? message.trim() : null;
};

const AutomationList = ({
  automations,
  loading,
  pagination,
  onPageChange,
  runningId,
  onCreate,
  onEdit,
  onRun,
  onDelete,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async (row) => {
    const id = row?.id ?? row?.fullData?.id;
    if (!id) return;

    setIsDeleting(true);
    try {
      await onDelete(id);
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const activeCount = automations.filter((item) =>
    isActiveAutomationStatus(item.status)
  ).length;

  const currentPage = pagination?.current_page ?? 1;
  const totalPages = pagination?.total_pages ?? 1;
  const totalCount = pagination?.total_count ?? automations.length;
  const perPage = pagination?.per_page ?? 20;
  const showPagination = totalCount > 0;
  const rangeStart = (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalCount);

  return (
    <>
      <div className="automation-list-header">
        <div>
          <h1 className="automation-page-title">Automations</h1>
          <p className="automation-list-subtitle">
            Schedule workflows, run them on demand, and manage configuration.
          </p>
        </div>
        <button type="button" className="automation-btn-primary" onClick={onCreate}>
          <Plus size={16} />
          Create automation
        </button>
      </div>

      {totalCount > 0 && (
        <div className="automation-stats">
          <div className="automation-stat-card">
            <span className="automation-stat-value">{totalCount}</span>
            <span className="automation-stat-label">Total workflows</span>
          </div>
          <div className="automation-stat-card automation-stat-active">
            <span className="automation-stat-value">{activeCount}</span>
            <span className="automation-stat-label">Running / pending</span>
          </div>
        </div>
      )}



      {totalCount === 0 && !loading ? (
        <div className="automation-empty-state">
          <div className="automation-empty-icon">
            <Zap size={28} />
          </div>
          <h2>No automations yet</h2>
          <p>Create your first workflow to run agents on a schedule or manually.</p>
          <button type="button" className="automation-btn-primary" onClick={onCreate}>
            <Plus size={16} />
            Create automation
          </button>
        </div>
      ) : (
        <div className={`automation-table-wrap ${loading ? "automation-table-loading" : ""}`}>
          {loading && (
            <div className="automation-table-overlay">
              <RefreshCw size={18} className="automation-spin" />
            </div>
          )}
          <table className="automation-table">
            <thead>
              <tr>  
                <th>Workflow</th>
                <th>Scheduler</th>
                <th>Status</th>
                <th>Last run</th>
                <th className="automation-table-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {automations.map((item) => {
                const id = item.id;
                const status = (item.status ?? "").toLowerCase();
                const isActive = isActiveAutomationStatus(item.status);
                const isRunLoading = runningId === id;
                const errorMessage = getAutomationError(item);
                const showError = status === "failed" && errorMessage;

                return (
                  <tr key={id} className={isActive ? "automation-row-active" : ""}>
                    <td>
                      <div className="automation-table-workflow">
                        <span className="automation-table-name">
                          {item.workflow_name ?? item.name ?? `Automation #${id}`}
                        </span>
                        {item.workflow_key && (
                          <span className="automation-workflow-badge">
                            {getWorkflowKeyLabel(item.workflow_key)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="automation-table-cell-meta">
                        <Clock size={13} />
                        {formatScheduler(item.scheduler_time)}
                      </span>
                    </td>
                    <td>
                      <div className="automation-status-cell">
                        <span
                          className={`automation-status-badge automation-status-${status || "active"}`}
                        >
                          {isActive && (
                            <RefreshCw
                              size={12}
                              className="automation-spin automation-status-icon"
                            />
                          )}
                          {item.status ?? "—"}
                        </span>
                        {showError && (
                          <span
                            className="automation-error-trigger"
                            data-tooltip={errorMessage}
                            role="img"
                            aria-label={`Error: ${errorMessage}`}
                            tabIndex={0}
                          >
                            <AlertCircle size={15} />
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="automation-table-cell-meta">
                        <Calendar size={13} />
                        {formatDateTime(item.started_at)}
                      </span>
                    </td>
                    <td>
                      <div
                        className="automation-action-group"
                        role="group"
                        aria-label="Automation actions"
                      >
                        <button
                          type="button"
                          className="automation-action-item automation-action-run"
                          onClick={() => onRun(id)}
                          disabled={isRunLoading || isActive}
                          data-tooltip={
                            isRunLoading
                              ? "Starting..."
                              : isActive
                                ? "Already running or pending"
                                : "Start automation"
                          }
                          aria-label="Start automation"
                        >
                          <Play size={15} className={isRunLoading ? "automation-spin" : ""} />
                        </button>
                        <button
                          type="button"
                          className="automation-action-item"
                          onClick={() => onEdit(id)}
                          disabled={isActive}
                          data-tooltip="Edit"
                          aria-label="Edit automation"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          className="automation-action-item automation-action-delete"
                          onClick={() =>
                            setDeleteTarget({ id, workflow_name: item.workflow_name })
                          }
                          disabled={isActive}
                          data-tooltip="Delete"
                          aria-label="Delete automation"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showPagination && (
        <div className="automation-pagination">
          <span className="automation-pagination-info">
            Showing {rangeStart}–{rangeEnd} of {totalCount}
          </span>
          <div className="automation-pagination-controls">
            <span className="automation-pagination-page">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="automation-pagination-btn"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="automation-pagination-btn"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        show={Boolean(deleteTarget)}
        onHide={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        rowData={{
          documentId: deleteTarget?.workflow_name ?? "this automation",
          id: deleteTarget?.id,
        }}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default AutomationList;
